import {useEffect, useState} from "react";
import {Box, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Typography } from "@mui/material";
import ProductAuthenticationFilterComponent from '../authentication-filters/product-authentication-filter.component';
import useAuthentication from '../../../hooks/useAuthentication';
import AuthenticationListSkeletonComponent from '../../skeletons/authentication-list-skeleton.component';
import moment from "moment";
import ProductAuthenticationViewDialog from "../dialog/product-authentication-view.dialog";
import Tooltip, {tooltipClasses} from "@mui/material/Tooltip";
import {styled} from "@mui/system";
import useFilters from "../../../hooks/useFilters";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{popper: className}} />

    ))(({theme}) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#fff",
            color: "rgba(51, 51, 207, 0.808)",
            boxShadow: "0px 5px 5px -3px rgb(0 0 0 / 10%), 0px 8px 10px 1px rgb(0 0 0 / 4%), 0px 3px 14px 2px rgba(0,0,0,0.12)",
            fontSize: 13,
            padding: theme.spacing(2),
            borderRadius: theme.shape.borderRadius, 
        },
  
        "& .MuiTooltip-arrow": {
            color: "#fff", 

            "&:before": {
                //
            }
        }
    })
);


const ProductAuthenticationTableHeader = () => {
    const tablecell = [
        { label: 'Product ID' },
        { label: 'Product Name' },
        { label: 'Document Reference' },
        { label: 'OVCODE Reference' },
        { label: 'Authentication Status' },
        { label: 'Manufacturer' },
        { label: 'Product Image' },
        { label: 'Product Type' },
        { label: 'Timestamp' }

    ]

    return (
        <TableHead>
            <TableRow>
                {tablecell.map((value, index) => {
                    return (
                        <TableCell sx={{ fontWeight: 700, color: '#3333CFCE', backgroundColor: "#D8D8FD", borderTop: "1px solid #7A78F7",
                            borderBottom: "1px solid #7A78F7", borderLeft: (index === 0) ? "1px solid #7A78F7" : {}, borderTopLeftRadius: (index === 0) ? "8px" : {},
                            borderRight: (index === 8) ? "1px solid #7A78F7" : {}, borderTopRightRadius: (index === 8) ? "2px" : {}}}
                        >
                            {value.label}
                        </TableCell>
                    )
                })}
            </TableRow>
        </TableHead>
    )
}


const ProductAuthenticationTable = (props) => {
    const {data, setOpenProductView, setProductId, index} = props;

    const handleOpenDetailView = (e, id) => {
        setOpenProductView(true);
        setProductId(id);
    }

    return (
        <TableRow key={index} sx={{cursor: "pointer", '&:last-child td, &:last-child th': {border: 0}, '& td, & th': {borderBottom: 0}}} onClick={(e) => handleOpenDetailView(e, data?.id)}>
            <TableCell component="th" scope="row">{data?.product_id}</TableCell>
            <TableCell component="th" scope="row">{data?.product_name}</TableCell>
            <TableCell component="th" scope="row">{data?.document_reference ? data?.document_reference : "N / A"}</TableCell>
            <TableCell component="th" scope="row">{data?.ovcode_reference}</TableCell>
            <TableCell component="th" scope="row" sx={{color: data?.authentication_status === "Completed" || data?.authentication_status === "Success"  ? '#40BF7E' : data?.authentication_status === "Pending" ?  "#FFA500"  : data?.authentication_status === "Suspicious" || data?.authentication_status === "Error" ? '#E76C59' : '#E87867' }}>
                {data?.authentication_status}
            </TableCell>
            <TableCell component="th" scope="row">{data?.manufacturer}</TableCell>
            <TableCell component="th" scope="row">{data?.image_url ? data?.image_url : "N / A"}</TableCell>
            <TableCell component="th" scope="row">
                <LightTooltip arrow placement="top" title={(data?.type === "RL") ? "Retail Level" : (data?.type === "BL") ? "Box Level" : "Mother Pallet Level"}>
                    <span>{data?.type}</span>
                </LightTooltip>
            </TableCell>
            <TableCell component="th" scope="row">{moment(data?.created_time).format("MMMM DD, YYYY h:mm:ss A")}</TableCell>
        </TableRow>
    )
}


const ProductAuthenticationComponent = (props) => {

    const {authenticationProductList, isFetchingAuthenticationProductList, pagination,  getProductAuthenticationLogs} = useAuthentication();

    const {getListFilterProduct, listProducts} = useFilters();

    const [page, setPage] = useState(1);
    const [product_type, setProductType] = useState(null);
    const [product_name, setProductName] = useState(null);
    const [product_reference, setProductReference] = useState(null);

    const [productId, setProductId] = useState(null);
    const [openProductView, setOpenProductView] = useState(false);
    

    useEffect(() => {
        getListFilterProduct();
    }, [])

    useEffect(() => {
        const limit = 10;

        if (product_name && !product_type && !product_reference) {
            let parameters = {
                product_code: product_name?.value,
                page: page,
                limit: limit,
                search_type: 1
            }

            getProductAuthenticationLogs(parameters);

        } else if (!product_name && product_type && !product_reference) {
            let parameters = {
                type: product_type,
                page: page,
                limit: limit,
                search_type: 2
            }

            getProductAuthenticationLogs(parameters);

        } else if (!product_name && !product_type && product_reference) {
            let parameters = {
                ovi: product_reference,
                page: page,
                limit: limit,
                search_type: 3
            }

            getProductAuthenticationLogs(parameters);

        } else if (product_name && product_type && !product_reference) {
            let parameters = {
                product_code: product_name?.value,
                type: product_type,
                page: page,
                limit: limit,
                search_type: 4
            }

            getProductAuthenticationLogs(parameters);

        } else if (product_name && product_type && product_reference) {
            let parameters = {
                product_code: product_name?.value,
                type: product_type,
                ovi: product_reference,
                page: page,
                limit: limit,
                search_type: 5
            }

            getProductAuthenticationLogs(parameters);

        } else if (!product_name && product_type && product_reference) {
            let parameters = {
                type: product_type,
                ovi: product_reference,
                page: page,
                limit: limit,
                search_type: 6
            }

            getProductAuthenticationLogs(parameters);

        } else if (product_name && !product_type && product_reference) {
            let parameters = {
                product_code: product_name?.value,
                ovi: product_reference,
                page: page,
                limit: limit,
                search_type: 7
            }

            getProductAuthenticationLogs(parameters);

        } else if (!product_name && !product_type && !product_reference) {
            let parameters = {
                page: page,
                limit: limit,
                search_type: 0
            }

            getProductAuthenticationLogs(parameters);
        }
    }, [product_name, product_type, product_reference, page])


    const handleKeyDownProductType = (event) => {
        if (event.key === 'Enter') {
            const name = event.target.value;
            setProductType(name);
        } else if (event.key === 'Backspace') {
            setTimeout(() => {
                const name = event.target.value;
                if (name === "") {
                    setProductType("");
                }
            }, 0);
        } else {
            setProductType(event.target.value);
        }
    }

    const handleKeyDownProductReference = (event) => {
        if (event.key === 'Enter') {
            const name = event.target.value;
            setProductReference(name);
        } else if (event.key === 'Backspace') {
            setTimeout(() => {
                const name = event.target.value;
                if (name === "") {
                    setProductReference("");
                }
            }, 0);
        }
    }

    const handleSelectProductFilter = (selectedOption) => {
        if (!selectedOption) {
            setProductName("");

        } else {
            setProductName(selectedOption);
        }
    }

    console.log("authenticationProductList bb", authenticationProductList)
    

    return (
        <Box>
            <Grid item xs={12} sx={{mb: 2, mt: 1.5}}>
                <ProductAuthenticationFilterComponent 
                    handleKeyDownProductType={handleKeyDownProductType} 
                    product_type={product_type}
                    product_name={product_name}
                    handleKeyDownProductReference={handleKeyDownProductReference}
                    product_reference={product_reference}
                    listProducts={listProducts}
                    handleSelectProductFilter={handleSelectProductFilter}
                />
            </Grid>

            <TableContainer component={Paper} sx={{border: "1px solid #3333cf54", borderRadius: 2, width: "100%", mt: 1 }}>
                <Table stickyHeader aria-label="sticky table" sx={{ minWidth: 650 }}>
                    <ProductAuthenticationTableHeader />
                    {isFetchingAuthenticationProductList ? (
                            <TableBody>
                                <AuthenticationListSkeletonComponent name={'Product'} />
                            </TableBody>
                                               
                    ) : (
                        <TableBody>
                            {(authenticationProductList?.length > 0) ?
                                authenticationProductList?.map((data, index) => (
                                    <ProductAuthenticationTable data={data} index={index} setOpenProductView={setOpenProductView} setProductId={setProductId} />
                                )) 
                            :
                                <TableRow sx={{'&:last-child td, &:last-child th': {border: 0}, '& td, & th': {borderBottom: 0}}}>
                                    <TableCell colSpan={9} align="center">
                                        <Typography sx={{fontSize: 16, color: "#58565f", padding: "40px 40px 20px 40px"}}>
                                            No available data to display
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    )}
                </Table>

                {!isFetchingAuthenticationProductList && authenticationProductList?.length !== 0 &&
                    <Pagination
                        count={pagination}
                        page={page}
                        // onChange={}
                        style={{ margin: "auto", width: "fit-content", paddingTop: 30, paddingBottom: 30 }}
                    />
                }
            </TableContainer>

            {openProductView &&
                <ProductAuthenticationViewDialog 
                    openProductView={openProductView} 
                    setOpenProductView={setOpenProductView} 
                    productId={productId}
                />
            }
        </Box>
    );
}

export default ProductAuthenticationComponent
